import type { ComponentProps, ReactNode } from "react";
import { cn } from "@/lib/utils";

type InputProps = ComponentProps<"input"> & {
  fullWidth?: boolean;
  /** Icon pinned to the right edge; pass onEndIconClick to make it the click target (see CLAUDE.md). */
  endIcon?: ReactNode;
  onEndIconClick?: () => void;
  /** Decorative content pinned to the left edge, e.g. a "$" prefix — never interactive. */
  startIcon?: ReactNode;
};

function Input({
  className,
  type,
  fullWidth = true,
  endIcon,
  onEndIconClick,
  startIcon,
  onWheel,
  onKeyDown,
  ref,
  ...props
}: InputProps) {
  const input = (
    <input
      ref={ref}
      type={type}
      onWheel={type === "number" ? (e) => e.currentTarget.blur() : onWheel}
      onKeyDown={
        type === "number"
          ? (e) => {
              if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
              onKeyDown?.(e);
            }
          : onKeyDown
      }
      className={cn(
        "placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground border-input flex h-9 min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
        "aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
        endIcon && "pr-9",
        startIcon && "pl-6",
        fullWidth ? "w-full" : "",
        className
      )}
      {...props}
    />
  );

  if (!endIcon && !startIcon) return input;

  return (
    <div className={cn("relative", fullWidth && "w-full")}>
      {input}
      {startIcon && (
        <div className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm [&>svg]:size-4">
          {startIcon}
        </div>
      )}
      {endIcon && (
        <div
          onClick={onEndIconClick}
          className={cn(
            "text-muted-foreground absolute top-1/2 right-3 -translate-y-1/2 [&>svg]:size-4",
            onEndIconClick ? "cursor-pointer" : "pointer-events-none"
          )}
        >
          {endIcon}
        </div>
      )}
    </div>
  );
}

export { Input };
